import React, { FC, useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import { routes } from "@app/routing";
import { CancerStage } from "@app/provider/types";
import { GisIntakeStep, STEP_ROUTE_SUFFIX } from "@app/service/intakeForm";
import { GIS_NO_ORDER_UID } from "@app/utils/gisEntrySource";
import { IntakeFormStepLayout } from "../components";
import { useIntakeForm } from "../context";

const messages = defineMessages({
  title: {
    id: "intakeStepCancerStage.title",
    defaultMessage: "What is your cancer stage?",
  },
  stageI: {
    id: "intakeStepCancerStage.stageI",
    defaultMessage: "I",
  },
  stageII: {
    id: "intakeStepCancerStage.stageII",
    defaultMessage: "II",
  },
  stageIII: {
    id: "intakeStepCancerStage.stageIII",
    defaultMessage: "III",
  },
  stageIV: {
    id: "intakeStepCancerStage.stageIV",
    defaultMessage: "IV",
  },
  noCancer: {
    id: "intakeStepCancerStage.noCancer",
    defaultMessage: "I do not have cancer",
  },
  nextButton: {
    id: "intakeStepCancerStage.nextButton",
    defaultMessage: "Next",
  },
});

// "noCancer" is a distinct selection (persisted as CancerStage.OTHER, which
// `buildAcuitySchedulingUrl` maps to the Acuity "I do not have cancer" stage
// label), so it must be tracked separately from "not yet answered".
type StageKey =
  | CancerStage.I
  | CancerStage.II
  | CancerStage.III
  | CancerStage.IV
  | "noCancer";

const IntakeStepCancerStage: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { orderUidPublic, testUid } = useParams<{
    orderUidPublic: string;
    testUid: string;
  }>();
  const {
    prefilledData,
    updatePrefilledField,
    setCurrentStep,
    totalSteps,
    getNextStep,
    getPreviousStep,
  } = useIntakeForm();

  const currentStep = GisIntakeStep.CANCER_STAGE;
  const isNoOrder = orderUidPublic === GIS_NO_ORDER_UID;

  const stageFromPrefill = (): StageKey | null => {
    switch (prefilledData?.cancerStage) {
      case CancerStage.I:
        return CancerStage.I;
      case CancerStage.II:
        return CancerStage.II;
      case CancerStage.III:
        return CancerStage.III;
      case CancerStage.IV:
        return CancerStage.IV;
      case CancerStage.OTHER:
        return "noCancer";
      default:
        return null;
    }
  };

  const [selectedKey, setSelectedKey] = useState<StageKey | null>(
    stageFromPrefill()
  );

  useEffect(() => {
    setCurrentStep(currentStep);
  }, [setCurrentStep]);

  const buildRoute = (step: GisIntakeStep | null) =>
    routes.scheduleAGeneticInformationSessionIntakePage(
      orderUidPublic || "",
      testUid || ""
    ) + (step ? STEP_ROUTE_SUFFIX[step] : "");

  const handleSelect = (key: StageKey) => {
    setSelectedKey(key);
    updatePrefilledField(
      "cancerStage",
      key === "noCancer" ? CancerStage.OTHER : (key as CancerStage)
    );
  };

  const handleContinue = () => {
    if (selectedKey === null) return;
    navigate(buildRoute(getNextStep(currentStep)));
  };

  const handleBack = () => {
    navigate(buildRoute(getPreviousStep(currentStep)));
  };

  if (!isNoOrder) {
    return <Navigate to={buildRoute(GisIntakeStep.CONFIRM_INFO)} replace />;
  }

  const options: { key: StageKey; label: string }[] = [
    { key: CancerStage.I, label: intl.formatMessage(messages.stageI) },
    { key: CancerStage.II, label: intl.formatMessage(messages.stageII) },
    { key: CancerStage.III, label: intl.formatMessage(messages.stageIII) },
    { key: CancerStage.IV, label: intl.formatMessage(messages.stageIV) },
    { key: "noCancer", label: intl.formatMessage(messages.noCancer) },
  ];

  return (
    <IntakeFormStepLayout
      currentStep={currentStep}
      totalSteps={totalSteps}
      title={intl.formatMessage(messages.title)}
      onContinue={handleContinue}
      onBack={handleBack}
      continueDisabled={selectedKey === null}
      continueLabel={intl.formatMessage(messages.nextButton)}
    >
      <div className="intake-form-step__radio-list">
        {options.map((option) => (
          <label
            key={option.key}
            className="intake-form-step__radio-list__item"
          >
            <input
              type="radio"
              name="cancer-stage"
              className="intake-form-step__radio-list__input"
              checked={selectedKey === option.key}
              onChange={() => handleSelect(option.key)}
            />
            <span className="intake-form-step__radio-list__label">
              {option.label}
            </span>
          </label>
        ))}
      </div>
    </IntakeFormStepLayout>
  );
};

export default IntakeStepCancerStage;
